import React, { Component } from "react";
import {
  GET,
  GET_FILE,
  alert,
  saveAs,
  loading,
  NavbarCustomer,
  t,
} from "../../components/CustomComponent.js"; // ปรับ Path ให้ตรงกับโปรเจกต์ของคุณ
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler } from "chart.js";
import { Line, Doughnut } from "react-chartjs-2";
import { getProfile } from "../../utils/localStorage";
import { isCustomerScopeReady } from "../../utils/customerScope";
import {
  REPORT_PERIODS,
  CHART_COLORS,
  formatDate,
  formatMinutes,
  formatMonthLabel,
  formatPercent
} from "../../utils/customerReport";

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler);

const LINE_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { y: { beginAtZero: true, max: 100, ticks: { callback: (value) => `${value}%` } } },
};

const DONUT_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: "65%",
  plugins: { legend: { position: "bottom", labels: { boxWidth: 12, font: { size: 11 } } } },
};

// สี badge ของ % SLA - ต่ำกว่าเป้าถือว่าเตือน
const SLA_TARGET = 95;
const slaColor = (value) => {
  if (value === null || value === undefined) return "secondary";
  return Number(value) < SLA_TARGET ? "warning" : "success";
};

export default class SlaReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // -----------------------------------
      // Filter States
      // -----------------------------------
      filter_period: "this_month",
      filter_priority: "",

      summary: {
        overall_sla: null,
        response_sla: null,
        resolution_sla: null,
        avg_resolution_minutes: null,
        breaches: 0,
        measured_requests: 0,
        total_requests: 0,
      },

      trend: [],
      by_priority: [],
      categories: [],
      breaches: [],
      lookups: { priorities: [] },
      exporting: "",
      loaded: false,
    };
  }

  async componentDidMount() {
    if (!isCustomerScopeReady(getProfile())) {
      loading(false);
      return;
    }

    await Promise.all([this.GetLookups(), this.Get()]);
    loading(false);
  }

  GetLookups = async () => {
    const result = await GET("customer/reports/lookups");
    if (result?.status === true) this.setState({ lookups: result.data || { priorities: [] } });
  };

  filterParams = () => ({
    period: this.state.filter_period,
    priority_uid: this.state.filter_priority || "",
  });

  Get = async () => {
    loading(true);

    const result = await GET("customer/reports/sla-report", this.filterParams());

    if (result?.status === true) {
      const data = result.data || {};
      this.setState({
        summary: data.summary || this.state.summary,
        trend: Array.isArray(data.trend) ? data.trend : [],
        by_priority: Array.isArray(data.by_priority) ? data.by_priority : [],
        categories: Array.isArray(data.categories) ? data.categories : [],
        breaches: Array.isArray(data.breaches) ? data.breaches : [],
        loaded: true,
      });
    }

    loading(false);
  };

  exportFile = async (format) => {
    this.setState({ exporting: format });

    const result = await GET_FILE("customer/reports/sla-report/export", {
      ...this.filterParams(),
      format,
      lang: localStorage.getItem("lang") === "EN" ? "en" : "th",
    });

    this.setState({ exporting: "" });

    if (result?.status !== true) {
      alert("", "error", t("error"), result?.message || t("export_failed"));
      return;
    }

    const stamp = new Date().toISOString().slice(0, 10);
    saveAs(result.blob, `sla-report_${stamp}.${format === "pdf" ? "pdf" : "xlsx"}`);
  };

  trendData = () => ({
    labels: this.state.trend.map((row) => formatMonthLabel(row.month)),
    datasets: [
      {
        label: t("response_sla"),
        data: this.state.trend.map((row) => row.response_sla),
        borderColor: CHART_COLORS[1],
        backgroundColor: "rgba(34, 197, 94, 0.15)",
        fill: true,
        tension: 0.35,
        spanGaps: true,
      },
    ],
  });

  priorityData = () => ({
    labels: this.state.by_priority.map((row) => row.key),
    datasets: [
      {
        data: this.state.by_priority.map((row) => row.sla),
        backgroundColor: this.state.by_priority.map((_, index) => CHART_COLORS[index % CHART_COLORS.length]),
        borderWidth: 0,
      },
    ],
  });

  render() {
    const { summary, categories, breaches, trend, by_priority, lookups, filter_period, filter_priority, exporting, loaded } = this.state;

    return (
      <div
        className="w-100 min-vh-100 pb-5"
        style={{ backgroundColor: "#F4F7FE" }}
      >
        <NavbarCustomer />
        <div className="body px-4 mt-3">
          {/* Header Section */}
          <div className="mb-4">
            <h3 className="fw-bold mb-1 text-dark">
              {t("sla_performance_report")}
            </h3>
            <div className="text-secondary text-14">
              {t("sla_metrics_desc")}
            </div>
          </div>

          {/* Filter Bar */}
          <div className="bg-white rounded-4 shadow-sm p-3 mb-4 d-flex flex-wrap align-items-end gap-3 border border-light">
            <div className="flex-grow-1" style={{ minWidth: "200px" }}>
              <label className="form-label text-secondary text-12 fw-bold mb-2">
                {t("period")}
              </label>
              <select
                className="form-select rounded-3 border-light bg-light text-dark fw-bold text-13 shadow-none"
                value={filter_period}
                onChange={(e) => this.setState({ filter_period: e.target.value })}
              >
                {REPORT_PERIODS.map((period) => (
                  <option key={period.value} value={period.value}>{t(period.label)}</option>
                ))}
              </select>
            </div>
            <div className="flex-grow-1" style={{ minWidth: "200px" }}>
              <label className="form-label text-secondary text-12 fw-bold mb-2">
                {t("priority")}
              </label>
              <select
                className="form-select rounded-3 border-light bg-light text-dark fw-bold text-13 shadow-none"
                value={filter_priority}
                onChange={(e) => this.setState({ filter_priority: e.target.value })}
              >
                <option value="">{t("all_priorities")}</option>
                {lookups.priorities?.map((priority) => (
                  <option key={priority.uid} value={priority.uid}>{priority.name}</option>
                ))}
              </select>
            </div>
            <button
              className="btn btn-primary px-4 fw-bold rounded-3 shadow-sm border-0 d-flex align-items-center"
              style={{ height: "42px" }}
              onClick={() => this.Get()}
            >
              <span className="icon me-2">{"\uf0b0"}</span> {t("apply_filters")}
            </button>
            <button
              className="btn btn-white border px-3 fw-bold rounded-3 d-flex align-items-center text-success shadow-none"
              style={{ height: "42px" }}
              disabled={!!exporting}
              onClick={() => this.exportFile("excel")}
            >
              <span className="icon me-2">{"\uf1c3"}</span> {exporting === "excel" ? t("loading") : t("export_excel")}
            </button>
          </div>

          {/* KPI Summary Cards */}
          <div className="row g-3 mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-primary rounded-4 shadow-sm p-3 bg-white h-100">
                <div className="text-primary text-12 fw-bold mb-1">
                  {t("overall_sla")}
                </div>
                <h2 className="fw-bold text-dark mb-1">{formatPercent(summary.overall_sla)}</h2>
                <div className="progress" style={{ height: "6px" }}>
                  <div
                    className="progress-bar"
                    style={{ width: `${Number(summary.overall_sla) || 0}%`, backgroundColor: "#2D9C8B" }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-success rounded-4 shadow-sm p-3 bg-white h-100">
                <div className="text-success text-12 fw-bold mb-1">
                  {t("response_sla")}
                </div>
                <h2 className="fw-bold text-dark mb-1">{formatPercent(summary.response_sla)}</h2>
                <div className="text-success text-10 fw-bold">
                  {t("target")}: {SLA_TARGET}%
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-warning rounded-4 shadow-sm p-3 bg-white h-100">
                <div className="text-warning text-12 fw-bold mb-1">
                  {t("avg_resolution")}
                </div>
                <h2 className="fw-bold text-dark mb-1">
                  {formatMinutes(summary.avg_resolution_minutes)}
                </h2>
                <div className="text-warning text-10 fw-bold">
                  {t("no_resolution_target")}
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card border-0 border-start border-5 border-danger rounded-4 shadow-sm p-3 bg-white h-100">
                <div className="text-danger text-12 fw-bold mb-1">
                  {t("sla_breaches")}
                </div>
                <h2 className="fw-bold text-dark mb-1">{summary.breaches}</h2>
                <div className="text-danger text-10 fw-bold">
                  {t("measured")}: {summary.measured_requests}/{summary.total_requests}
                </div>
              </div>
            </div>
          </div>

          {/* Graphs Area */}
          <div className="row g-4 mb-4">
            <div className="col-lg-8">
              <div className="card border-0 rounded-4 shadow-sm p-4 bg-white h-100">
                <h6 className="fw-bold text-dark mb-4">{t("sla_trend")}</h6>
                <div style={{ height: "250px" }}>
                  {trend.length === 0 ? (
                    <div className="d-flex align-items-center justify-content-center bg-light rounded-3 h-100 text-secondary">
                      {loaded ? t("no_data") : t("loading")}
                    </div>
                  ) : (
                    <Line data={this.trendData()} options={LINE_OPTIONS} />
                  )}
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="card border-0 rounded-4 shadow-sm p-4 bg-white h-100">
                <h6 className="fw-bold text-dark mb-4">
                  {t("sla_by_priority")}
                </h6>
                <div style={{ height: "250px" }}>
                  {by_priority.length === 0 ? (
                    <div className="d-flex align-items-center justify-content-center h-100 text-secondary">
                      {loaded ? t("no_data") : t("loading")}
                    </div>
                  ) : (
                    <Doughnut data={this.priorityData()} options={DONUT_OPTIONS} />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Details Table */}
          <div className="card border-0 rounded-4 shadow-sm pb-2 overflow-hidden bg-white mb-4">
            <div className="p-4 border-bottom border-light">
              <h6 className="fw-bold text-dark mb-0">
                {t("sla_metrics_by_category")}
              </h6>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr className="text-secondary fw-bold text-12 border-bottom border-light bg-light bg-opacity-50">
                    <th className="py-3 px-4 border-0">{t("category")}</th>
                    <th className="py-3 border-0">{t("total_requests")}</th>
                    <th className="py-3 border-0">{t("avg_response")}</th>
                    <th className="py-3 border-0">{t("avg_resolution")}</th>
                    <th className="py-3 border-0">{t("response_sla")}</th>
                    <th className="py-3 border-0">{t("resolution_sla")}</th>
                    <th className="py-3 border-0">{t("breaches")}</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-secondary py-4">{loaded ? t("no_data") : t("loading")}</td>
                    </tr>
                  ) : categories.map((item, idx) => (
                    <tr key={idx} className="border-bottom border-light">
                      <td className="py-3 px-4 fw-bold text-dark text-13">
                        {item.category}
                      </td>
                      <td className="text-secondary text-13">{item.total_requests}</td>
                      <td className="text-secondary text-13">
                        {formatMinutes(item.avg_response_minutes)}
                      </td>
                      <td className="text-secondary text-13">
                        {formatMinutes(item.avg_resolution_minutes)}
                      </td>
                      <td>
                        <span className={`badge bg-${slaColor(item.response_sla)}-light text-${slaColor(item.response_sla)} rounded-pill px-3 py-1 fw-bold text-10 border border-${slaColor(item.response_sla)}`}>
                          {formatPercent(item.response_sla)}
                        </span>
                      </td>
                      <td>
                        <span className="badge bg-light text-secondary rounded-pill px-3 py-1 fw-bold text-10 border">
                          {formatPercent(item.resolution_sla)}
                        </span>
                      </td>
                      <td className="text-secondary text-13 fw-bold">
                        {item.breaches}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* SLA Breach Details Table */}
          <div className="border border-danger rounded-4 overflow-hidden">
            <div className="p-3" style={{ backgroundColor: "#FFF5F5" }}>
              <h6 className="fw-bold text-danger mb-0 text-13">
                <span className="icon me-2">{"\uf071"}</span>{" "}
                {t("sla_breach_details")}
              </h6>
            </div>
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr className="text-secondary text-11 fw-bold bg-light bg-opacity-25">
                    <th className="py-3 px-4 border-0">{t("request_id")}</th>
                    <th className="py-3 border-0">{t("date")}</th>
                    <th className="py-3 border-0">{t("category")}</th>
                    <th className="py-3 border-0">{t("breach_type")}</th>
                    <th className="py-3 border-0">{t("target")}</th>
                    <th className="py-3 border-0">{t("actual")}</th>
                    <th className="py-3 border-0">{t("priority")}</th>
                  </tr>
                </thead>
                <tbody>
                  {breaches.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-secondary py-4">{loaded ? t("no_sla_breach") : t("loading")}</td>
                    </tr>
                  ) : breaches.map((item, idx) => (
                    <tr key={idx} className="border-bottom border-light">
                      <td className="py-3 px-4 fw-bold text-danger text-13">
                        {item.wr_number || "-"}
                      </td>
                      <td className="text-secondary text-13">{formatDate(item.date)}</td>
                      <td className="text-secondary text-13">
                        {item.category || "-"}
                      </td>
                      <td>
                        <span className="badge bg-danger-light text-danger rounded-pill px-3 py-1 fw-bold text-10 border border-danger">
                          {t(item.breach_type)}
                        </span>
                      </td>
                      <td className="text-secondary text-13">{formatMinutes(item.target_minutes)}</td>
                      <td className="text-secondary text-13">{formatMinutes(item.actual_minutes)}</td>
                      <td className="text-secondary text-13 fw-medium">
                        {item.priority || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
